import { formatSlotRange } from '@betreuung/shared';
import { AlertCircle } from 'lucide-react';
import { AlertDialog, AlertDialogBox } from '@/components/ui/alert-dialog';
import { PersonChip } from '@/components/shared/PersonChip';
import { useAppData } from '@/app/appData';
import { de } from '@/i18n/de';
import { AssignmentModal } from './AssignmentModal';
import { useAssignmentFlows } from './useAssignmentFlows';

/**
 * Renders the modal and the three dialogs that belong to the assignment flow.
 * Mounted once in the shell so any view can start the flow.
 */
export function AssignmentFlowsHost() {
  const { pastConfirmation, confirmPast, cancelPast, conflict, dismissConflict, openConflicting, stale, dismissStale, openStale } =
    useAssignmentFlows();
  const { persons } = useAppData();

  return (
    <>
      <AssignmentModal />

      {/* Editing something that already happened asks first — the change is
          recorded in the log either way, and that is stated in the message. */}
      <AlertDialog open={pastConfirmation !== null}>
        {pastConfirmation ? (
          <AlertDialogBox
            title={de.pastDialog.title}
            cancelLabel={de.pastDialog.cancel}
            confirmLabel={de.pastDialog.confirm}
            onCancel={cancelPast}
            onConfirm={confirmPast}
          >
            {pastConfirmation.personId === null
              ? de.pastDialog.bodyWithoutPerson(
                  formatSlotRange(pastConfirmation.startSlot, pastConfirmation.endSlot, {
                    withYear: true,
                  }),
                )
              : de.pastDialog.body(
                  formatSlotRange(pastConfirmation.startSlot, pastConfirmation.endSlot, {
                    withYear: true,
                  }),
                  persons.describe(pastConfirmation.personId).name,
                )}
          </AlertDialogBox>
        ) : null}
      </AlertDialog>

      {/* Somebody else took the range while the draft was open. No merging and
          no overwriting: the draft is gone and the existing entry has to be
          opened deliberately. */}
      <AlertDialog open={conflict !== null}>
        {conflict ? (
          <AlertDialogBox
            title={
              <span className="flex items-center gap-2">
                <AlertCircle className="size-[18px] text-cal-9" />
                {de.conflictDialog.title}
              </span>
            }
            cancelLabel={de.conflictDialog.close}
            confirmLabel={de.conflictDialog.openExisting}
            onCancel={dismissConflict}
            onConfirm={openConflicting}
          >
            <p>{de.conflictDialog.intro}</p>
            <div className="my-2.5 flex items-center gap-2 rounded-control border border-line px-3 py-2.5">
              <PersonChip person={persons.describe(conflict.personId)} size="sm" />
              <span className="font-mono text-hint font-medium text-fg-secondary">
                {formatSlotRange(conflict.startSlot, conflict.endSlot)}
              </span>
            </div>
            <p>{de.conflictDialog.outro}</p>
          </AlertDialogBox>
        ) : null}
      </AlertDialog>

      {/* The very entry being edited changed underneath us. */}
      <AlertDialog open={stale !== null}>
        {stale ? (
          <AlertDialogBox
            title={
              <span className="flex items-center gap-2">
                <AlertCircle className="size-[18px] text-cal-9" />
                {de.staleDialog.title}
              </span>
            }
            cancelLabel={de.staleDialog.close}
            confirmLabel={stale.current ? de.staleDialog.openCurrent : de.staleDialog.close}
            onCancel={dismissStale}
            onConfirm={stale.current ? openStale : dismissStale}
          >
            {stale.current ? (
              <>
                <p>{de.staleDialog.intro}</p>
                <div className="my-2.5 flex items-center gap-2 rounded-control border border-line px-3 py-2.5">
                  <PersonChip person={persons.describe(stale.current.personId)} size="sm" />
                  <span className="font-mono text-hint font-medium text-fg-secondary">
                    {formatSlotRange(stale.current.startSlot, stale.current.endSlot)}
                  </span>
                </div>
              </>
            ) : (
              <p>{de.staleDialog.introDeleted}</p>
            )}
            <p>{de.staleDialog.outro}</p>
          </AlertDialogBox>
        ) : null}
      </AlertDialog>
    </>
  );
}
